export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const NAME_PATTERN = /^[\p{L}][\p{L}\s.'’-]*$/u;

const INSTALLATION_PREFERENCE_ID = /^[a-z][a-z0-9_]*$/;

export type LeadCaptureMode = "quiz" | "freeQuote" | "employeeRef";

export type LeadCaptureFields = {
  email: string;
  phone: string;
  name?: string;
  zip?: string;
  installationPreference?: string;
  employeePromoCode?: string;
  socialPlatformReferralSource?: string;
  socialPlatformReferralOtherDetail?: string;
};

export type LeadCaptureFieldErrors = Partial<
  Record<
    | "email"
    | "phone"
    | "name"
    | "zip"
    | "installationPreference"
    | "employeePromoCode"
    | "socialPlatformReferralSource"
    | "socialPlatformReferralOtherDetail",
    string
  >
>;

const SOCIAL_PLATFORM_ID =
  /^(facebook|instagram|tiktok|linkedin|x|youtube|other)$/;

/** Max length for the freeform "Other" referral source detail (MAR-485). */
export const SOCIAL_OTHER_DETAIL_MAX_LENGTH = 100;

export function parseLeadCaptureMode(
  value: FormDataEntryValue | null,
): LeadCaptureMode {
  return value === "freeQuote"
    ? "freeQuote"
    : value === "employeeRef"
      ? "employeeRef"
      : "quiz";
}

export function parseInstallationPreference(
  value: FormDataEntryValue | null,
): string | undefined {
  if (typeof value !== "string" || !INSTALLATION_PREFERENCE_ID.test(value)) {
    return undefined;
  }
  return value;
}

export function parseEmployeePromoCode(
  value: FormDataEntryValue | null,
): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

export function parseSocialPlatformReferralSource(
  value: FormDataEntryValue | null,
): string | undefined {
  if (typeof value !== "string" || !SOCIAL_PLATFORM_ID.test(value)) {
    return undefined;
  }
  return value;
}

export function parseSocialPlatformReferralOtherDetail(
  value: FormDataEntryValue | null,
): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, SOCIAL_OTHER_DETAIL_MAX_LENGTH);
}

/**
 * Value sent as REFRRING_SOURCE: freeform text when "Other" is selected,
 * otherwise the platform id.
 */
export function resolveSocialSourceForSubmit(
  socialPlatformReferralSource: string | undefined,
  socialPlatformReferralOtherDetail: string | undefined,
): string | undefined {
  if (socialPlatformReferralSource === "other") {
    return socialPlatformReferralOtherDetail;
  }
  return socialPlatformReferralSource;
}

export type ValidateLeadCaptureOptions = {
  requireInstallationPreference?: boolean;
  requireEmployeePromoCode?: boolean;
  requireSocialPlatformReferralSource?: boolean;
};

export function validateLeadCaptureFields(
  fields: LeadCaptureFields,
  mode: LeadCaptureMode,
  requireInstallationPreferenceOrOptions:
    | boolean
    | ValidateLeadCaptureOptions = false,
): LeadCaptureFieldErrors {
  const options: ValidateLeadCaptureOptions =
    typeof requireInstallationPreferenceOrOptions === "boolean"
      ? {
          requireInstallationPreference: requireInstallationPreferenceOrOptions,
        }
      : requireInstallationPreferenceOrOptions;

  const errors: LeadCaptureFieldErrors = {};
  const email = fields.email.trim();
  const phoneDigits = fields.phone.replace(/\D/g, "");

  // employeeRef uses the same contact + landing fields as freeQuote (F&F form).
  if (mode === "freeQuote" || mode === "employeeRef") {
    const name = fields.name?.trim() ?? "";
    if (!name) errors.name = "Name is required";
    else if (!NAME_PATTERN.test(name))
      errors.name = "Please enter a valid name";

    const zip = fields.zip?.trim() ?? "";
    if (!zip) errors.zip = "ZIP code is required";
    else if (!/^\d{5}$/.test(zip))
      errors.zip = "Please enter a valid 5-digit ZIP code";

    if (
      options.requireInstallationPreference &&
      !parseInstallationPreference(fields.installationPreference ?? null)
    ) {
      errors.installationPreference = "Installation preference is required";
    }

    if (options.requireEmployeePromoCode) {
      const promo = fields.employeePromoCode?.trim() ?? "";
      if (!promo) errors.employeePromoCode = "Employee promo code is required";
    }

    if (options.requireSocialPlatformReferralSource) {
      const platform = parseSocialPlatformReferralSource(
        fields.socialPlatformReferralSource ?? null,
      );
      if (!platform) {
        errors.socialPlatformReferralSource =
          "Social platform referral source is required";
      } else if (platform === "other") {
        const otherDetail =
          fields.socialPlatformReferralOtherDetail?.trim() ?? "";
        if (!otherDetail) {
          errors.socialPlatformReferralOtherDetail =
            "Please tell us how you heard about us";
        } else if (otherDetail.length > SOCIAL_OTHER_DETAIL_MAX_LENGTH) {
          errors.socialPlatformReferralOtherDetail = `Please enter ${SOCIAL_OTHER_DETAIL_MAX_LENGTH} characters or fewer`;
        }
      }
    }
  }

  if (!email) errors.email = "Email address is required";
  else if (!EMAIL_REGEX.test(email))
    errors.email = "Please enter a valid email address";

  if (!phoneDigits) errors.phone = "Phone number is required";
  else if (phoneDigits.length !== 10)
    errors.phone = "Please enter a valid 10-digit phone number";

  return errors;
}
